import useSWRMutation from "swr/mutation";
import { useAccountIds } from "../../../../auth/accessToken/components/withAccountIds";
import useStore from "../../../../common/hooks/useExternalStore";
import createExternalStore from "../../../../common/utils/createExternalStore";
import { CHECKOUT_API_HOSTNAME, CORE_API_HOSTNAME } from "../../../../env";
import fulfill from "../../../../fulfill";
import { CollectorConnectionCallback } from "../../../../types/management-auth/generated";
import useAccount from "../../../account/hooks/useAccount";

export type PatchedCollectorConnectionCallback = Omit<
    CollectorConnectionCallback,
    "password"
> & {
    password?: string;
};

export interface CollectorCredentialsData {
    name: string;
    data: {
        url: string;
        username: string;
        password: string;
        store_id: number;
        country: string;
        options?: {
            customer_client?: {
                customers_client_id: string;
                customers_client_secret: string;
                customers_client_audience: string;
            };
            enable_on_hold?: boolean;
            settlement_reference_fields?: string;
        };
    };
    description?: string;
    promote?: {
        type: "default" | "override";
        value: string;
        stores?: {
            store_id: number;
            country: string;
            segment: "B2C" | "B2B";
        }[];
    }[];
}

export enum ModalState {
    Closed = "Closed",
    PreApplicationInfo = "PreApplicationInfo",
    InitiateCallbackLoading = "InitiateCallbackLoading",
    CallbackSuccess = "CallbackSuccess",
    CallbackError = "CallbackError",
}

export interface State {
    modalState: ModalState;
    modalType: "swap_agreement" | "initial_connection_configuration";
}

export const initialState: State = {
    modalState: ModalState.Closed,
    modalType: "initial_connection_configuration",
};

const collectorModalStore = createExternalStore(
    initialState,
    {
        openModal: (state, modalType: State["modalType"]) => ({
            ...state,
            modalState: ModalState.PreApplicationInfo,
            modalType,
        }),
        closeModal: (state) => ({
            ...state,
            modalState: ModalState.Closed,
        }),
        setModalState: (state, modalState: State["modalState"]) => ({
            ...state,
            modalState,
        }),
    },
    "connections/collector/callback",
);

const fetchPostCallback = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            userId: string;
            body: PatchedCollectorConnectionCallback;
        };
    },
): Promise<boolean> => {
    const { accountId, body, userId } = options.arg;
    if (!accountId || !userId) return false;
    return await fulfill.post({
        accountId: accountId,
        json: {
            username: body.username,
            password: body.password,
            payment_options: body.payment_options,
            stores: body.stores,
        },
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/management/settings/connections/collector/${userId}`,
        handlers: {
            202: () => true,
            500: () => false,
            501: () => false,
            502: () => false,
            503: () => false,
            504: () => false,
        },
    });
};

const fetchPutCredentials = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            userId: string;
            data: CollectorCredentialsData;
        };
    },
): Promise<boolean> => {
    const { accountId, data, userId } = options.arg;
    if (!accountId || !userId) return false;
    return await fulfill.put({
        accountId: accountId,
        json: data,
        url: `${CHECKOUT_API_HOSTNAME}/v1/admin/gateways/collector/credentials`,
        handlers: {
            200: () => true,
            201: () => true,
            500: () => false,
            501: () => false,
            502: () => false,
            503: () => false,
            504: () => false,
        },
    });
};

export const useCollectorCallback = () => {
    const { prodAccountId, urlAccountId } = useAccountIds();
    const {
        state: { modalState, modalType },
        dispatch,
    } = useStore(collectorModalStore);
    const { account, mutate } = useAccount();
    const accountId = prodAccountId || urlAccountId;
    const {
        trigger: confirmConnection,
        isMutating: isConfirmingConnection,
        error: confirmConnectionError,
        data: confirmConnectionResponse,
    } = useSWRMutation(
        `${accountId}/payment-connections/collector/callback`,
        fetchPostCallback,
        {
            onSuccess: (response) => {
                if (response) {
                    mutate();
                    dispatch("setModalState", ModalState.CallbackSuccess);
                } else {
                    dispatch("setModalState", ModalState.CallbackError);
                }
            },
        },
    );
    const {
        trigger: createCredentials,
        isMutating: isCreatingCredentials,
        error: createCredentialsError,
        data: createCredentialsResponse,
    } = useSWRMutation(
        `${accountId}/payment-connections/collector/credentials`,
        fetchPutCredentials,
        {
            onSuccess: (response) => {
                if (response) {
                    mutate();
                    dispatch("setModalState", ModalState.CallbackSuccess);
                } else {
                    dispatch("setModalState", ModalState.CallbackError);
                }
            },
        },
    );

    const openModal = (modalType: State["modalType"]) => {
        dispatch("openModal", modalType);
    };

    const closeModal = () => {
        dispatch("closeModal");
    };

    const confirmCollectorConnection = async (payload: {
        userId: string;
        body: PatchedCollectorConnectionCallback;
    }) => {
        dispatch("setModalState", ModalState.InitiateCallbackLoading);
        confirmConnection({
            accountId,
            userId: payload.userId,
            body: payload.body,
        });
    };

    const createCollectorCredentials = async (payload: {
        userId: string;
        data: CollectorCredentialsData;
    }) => {
        dispatch("setModalState", ModalState.InitiateCallbackLoading);
        createCredentials({
            accountId,
            userId: payload.userId,
            data: payload.data,
        });
    };

    return {
        modalState,
        type: modalType,
        openModal,
        closeModal,
        account,
        confirmCollectorConnection,
        isConfirmingConnection,
        confirmConnectionError,
        confirmConnectionResponse,
        createCollectorCredentials,
        isCreatingCredentials,
        createCredentialsError,
        createCredentialsResponse,
    };
};
